import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Home, LogIn, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts';

const NotFoundContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[6]};
  background: ${({ theme }) => theme.colors.background};
  text-align: center;
`;

const ErrorCode = styled.h1`
  font-size: 120px;
  font-weight: ${({ theme }) => theme.typography.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  line-height: 1;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.typography['2xl']};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto ${({ theme }) => theme.spacing[6]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[8]};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const ActionButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.medium};
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
    transform: translateY(-1px);
  }
`;

const SecondaryButton = styled(ActionButton)`
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.gray50};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ContactInfo = styled.div`
  margin-top: ${({ theme }) => theme.spacing[8]};
  padding-top: ${({ theme }) => theme.spacing[6]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.typography.medium};

  &:hover {
    text-decoration: underline;
  }
`;

const NotFoundPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <NotFoundContainer>
      <ErrorCode>404</ErrorCode>
      <Title>Page Not Found</Title>
      <Description>
        Oops! The page you're looking for doesn't exist or has been moved.
        Let's get you back on track.
      </Description>

      <ActionButtons>
        <ActionButton to="/">
          <Home size={20} />
          Go to Home
        </ActionButton>
        
        {!user ? (
          <ActionButton to="/login">
            <LogIn size={20} />
            Login
          </ActionButton>
        ) : (
          <SecondaryButton to="/dashboard">
            <ArrowLeft size={20} />
            Back to Dashboard
          </SecondaryButton>
        )}
      </ActionButtons>

      <ContactInfo>
        <p>Need help? Contact our support team at</p>
        <ContactLink href="mailto:support@example.com">
          <Mail size={16} style={{ marginRight: '8px' }} />
          support@example.com
        </ContactLink>
      </ContactInfo>
    </NotFoundContainer>
  );
};

export default NotFoundPage;